import type { QueryClient } from "@tanstack/react-query";
import { api } from "../../web/lib/api";
import { isSaving } from "../../web/lib/saving";
import type { PriceListData, PriceListRows, RecordScope } from "./meta";

/*
 * Keeping the Price List's data up to date one record at a time. When records change (here, in
 * ServiceM8, or by someone else), the screens fetch just those records (rows.ts) and put them in
 * place of what they had, instead of fetching everything again: a webhook for one item moves one
 * item, however big the price list. Changes are gathered for a moment and fetched together; when
 * the same record is asked for again before an answer comes, only the newest answer is used.
 * Anything that affects every record (settings, groups, name tags, tax rates, a big batch) still
 * fetches everything, as does an answer that fails.
 */

/** A change to the Price List's data: these records (by kind), or "all". */
export type Change = RecordScope | "all";

const KINDS = ["material", "supplier", "materialbundle"] as const;
type Kind = (typeof KINDS)[number];
/** Records asked for at once (rows.ts takes as many); more: everything is fetched instead. */
const MAX = 500;
/** How long changes are gathered before they're fetched (a save and its live update come together). */
const GATHER_MS = 50;

interface Pending {
    all: boolean;
    records: Record<Kind, Set<string>>;
    timer?: ReturnType<typeof setTimeout>;
}
const pending = new Map<string, Pending>();
// The newest request for each record ("kind:uuid"): older answers for it aren't used.
const newest = new Map<string, number>();
let requests = 0;

export const priceListKey = (companyId: string) => ["c", companyId, "pricelist"];

/** These records changed (or "all"): fetched shortly, together with any other changes. */
export function queueChange(qc: QueryClient, companyId: string, change: Change) {
    const p = pending.get(companyId) ?? { all: false, records: { material: new Set(), supplier: new Set(), materialbundle: new Set() } };
    pending.set(companyId, p);
    if (change === "all") p.all = true;
    else {
        for (const [kind, uuids] of Object.entries(change)) {
            if (!KINDS.includes(kind as Kind)) p.all = true;
            else for (const uuid of uuids ?? []) p.records[kind as Kind].add(uuid);
        }
    }
    p.timer ??= setTimeout(() => void flushChanges(qc, companyId), GATHER_MS);
}

/** Fetch what's changed now: the records gathered, or everything. Waits while a save is in flight (it calls this when it's done). */
export async function flushChanges(qc: QueryClient, companyId: string) {
    const p = pending.get(companyId);
    if (!p) return;
    clearTimeout(p.timer);
    p.timer = undefined;
    const key = priceListKey(companyId);
    if (isSaving(key)) return;
    pending.delete(companyId);
    const scope = Object.fromEntries(KINDS.filter((k) => p.records[k].size).map((k) => [k, [...p.records[k]]])) as Record<Kind, string[]>;
    const many = Object.values(scope).some((uuids) => uuids.length > MAX);
    // Nothing loaded yet (it's being fetched whole), or too much to fetch by record: everything.
    if (p.all || many || !qc.getQueryData(key)) {
        if (p.all || many) await qc.invalidateQueries({ queryKey: key, exact: true });
        return;
    }
    if (!Object.keys(scope).length) return;
    const request = ++requests;
    for (const [kind, uuids] of Object.entries(scope)) for (const uuid of uuids) newest.set(`${kind}:${uuid}`, request);
    try {
        const rows = await api<PriceListRows>(`/c/${companyId}/tools/pricelist/rows`, { body: scope });
        qc.setQueryData<PriceListData>(key, (d) => (d ? applyRows(d, rows, (kind, uuid) => newest.get(`${kind}:${uuid}`) === request) : d));
    } catch {
        // Couldn't fetch them: everything, so nothing is left out of date.
        await qc.invalidateQueries({ queryKey: key, exact: true });
    } finally {
        for (const [kind, uuids] of Object.entries(scope)) for (const uuid of uuids) if (newest.get(`${kind}:${uuid}`) === request) newest.delete(`${kind}:${uuid}`);
    }
}

/** Put some records' fresh data in place of theirs (`current`: this answer is the newest asked for that record). */
export function applyRows(d: PriceListData, rows: PriceListRows, current: (kind: Kind, uuid: string) => boolean = () => true): PriceListData {
    let next = d;
    if (rows.material) {
        const r = rows.material;
        const uuids = new Set(r.uuids.filter((u) => current("material", u)));
        if (uuids.size) {
            const of = <T>(list: readonly T[], uuidOf: (x: T) => string) => list.filter((x) => uuids.has(uuidOf(x)));
            const keep = <T>(list: readonly T[], uuidOf: (x: T) => string) => list.filter((x) => !uuids.has(uuidOf(x)));
            const record = <T>(all: Record<string, T>, fresh: Record<string, T>) => ({
                ...Object.fromEntries(Object.entries(all).filter(([u]) => !uuids.has(u))),
                ...Object.fromEntries(Object.entries(fresh).filter(([u]) => uuids.has(u))),
            });
            next = {
                ...next,
                materials: replaceRows(next.materials, of(r.rows, (m) => m.uuid), uuids),
                checked: record(next.checked, r.checked),
                itemPricing: record(next.itemPricing, r.itemPricing),
                supplierPrices: [...keep(next.supplierPrices, (p) => p.materialUuid), ...of(r.supplierPrices, (p) => p.materialUuid)],
                costFollows: [...keep(next.costFollows, (u) => u), ...of(r.costFollows, (u) => u)],
                costPrices: record(next.costPrices, r.costPrices),
                costUpdates: record(next.costUpdates, r.costUpdates),
                costQuotes: record(next.costQuotes, r.costQuotes),
                costTyped: record(next.costTyped, r.costTyped),
                unknownItems: [...keep(next.unknownItems, (u) => u), ...of(r.unknownItems, (u) => u)],
                changedInServiceM8: { ...next.changedInServiceM8, material: [...keep(next.changedInServiceM8.material, (u) => u), ...of(r.changed, (u) => u)] },
                pricedInServiceM8: [...keep(next.pricedInServiceM8, (u) => u), ...of(r.pricedInServiceM8, (u) => u)],
                itemTags: record(next.itemTags, r.itemTags),
                lastUsed: next.lastUsed && r.lastUsed ? record(next.lastUsed, r.lastUsed) : next.lastUsed,
                packages: record(next.packages, r.packages),
            };
        }
    }
    for (const kind of ["supplier", "materialbundle"] as const) {
        const r = rows[kind];
        const uuids = new Set((r?.uuids ?? []).filter((u) => current(kind, u)));
        if (!r || !uuids.size) continue;
        const changed = [...next.changedInServiceM8[kind].filter((u) => !uuids.has(u)), ...r.changed.filter((u) => uuids.has(u))];
        const checked = { ...Object.fromEntries(Object.entries(next.checked).filter(([u]) => !uuids.has(u))), ...Object.fromEntries(Object.entries(r.checked).filter(([u]) => uuids.has(u))) };
        next =
            kind === "supplier"
                ? { ...next, suppliers: replaceRows(next.suppliers, rows.supplier?.rows ?? [], uuids), changedInServiceM8: { ...next.changedInServiceM8, supplier: changed }, checked }
                : {
                      ...next,
                      bundles: replaceRows(next.bundles, rows.materialbundle?.rows ?? [], uuids),
                      changedInServiceM8: { ...next.changedInServiceM8, materialbundle: changed },
                      checked,
                      // Its tags (item_tags, by its uuid).
                      itemTags: {
                          ...Object.fromEntries(Object.entries(next.itemTags).filter(([u]) => !uuids.has(u))),
                          ...Object.fromEntries(Object.entries(rows.materialbundle?.itemTags ?? {}).filter(([u]) => uuids.has(u))),
                      },
                  };
    }
    return next;
}

/**
 * A list (sorted by name, as the server sends it) with these records' rows replaced, added or
 * (not in \`fresh\`) removed, still in name order.
 */
function replaceRows<T extends { uuid: string; name: string }>(list: readonly T[], fresh: readonly T[], uuids: ReadonlySet<string>): T[] {
    const byUuid = new Map(fresh.filter((x) => uuids.has(x.uuid)).map((x) => [x.uuid, x]));
    const kept = list.filter((x) => !uuids.has(x.uuid) || byUuid.has(x.uuid)).map((x) => byUuid.get(x.uuid) ?? x);
    const added = [...byUuid.values()].filter((x) => !list.some((y) => y.uuid === x.uuid));
    const renamed = kept.some((x, i) => i > 0 && byName(kept[i - 1]!, x) > 0);
    return added.length || renamed ? [...kept, ...added].sort(byName) : kept;
}

const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name);
